import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import type { AdminUser } from '../../lib/api';
import type { UserRole } from '@asset-mgmt/shared';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const ROLE_COLORS: Record<UserRole, string> = {
  admin:  'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  editor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleChanging, setRoleChanging] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.listUsers().then(setUsers).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleRoleChange(userId: string, role: UserRole) {
    setRoleChanging(userId);
    try {
      const updated = await adminApi.setRole(userId, role);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, role: updated.role } : u));
      toast.success('Role updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change role');
    }
    setRoleChanging(null);
  }

  async function handleForceLogout(userId: string) {
    if (!confirm('Force-logout all sessions for this user?')) return;
    setLoggingOut(userId);
    try {
      const { revoked } = await adminApi.forceLogout(userId);
      toast.success(`Revoked ${revoked} session(s).`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to force-logout');
    }
    setLoggingOut(null);
  }

  async function handleDelete(userId: string, email: string) {
    if (!confirm(`Permanently delete user ${email} and all their data?`)) return;
    setDeleting(userId);
    try {
      await adminApi.deleteUser(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      toast.success(`User ${email} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete user');
    }
    setDeleting(null);
  }

  return (
    <div className="p-4 sm:p-6">
      <h1 className="mb-6 text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>

      {loading ? (
        <p className="text-gray-400">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-xs font-medium uppercase tracking-wide text-gray-400">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3">
                      <Link to={`/admin/users/${u.id}`} className="font-medium text-primary-600 hover:underline">
                        {u.displayName}
                      </Link>
                      <p className="text-xs text-gray-400">{u.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      {isSelf ? (
                        <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium capitalize ${ROLE_COLORS[u.role]}`}>
                          {u.role}
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          disabled={roleChanging === u.id}
                          onChange={(e) => void handleRoleChange(u.id, e.target.value as UserRole)}
                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-2 py-1 text-xs capitalize focus:ring-2 focus:ring-primary-200 dark:[color-scheme:dark]"
                        >
                          <option value="viewer">viewer</option>
                          <option value="editor">editor</option>
                          <option value="admin">admin</option>
                        </select>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{u.createdAt.slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <Link to={`/admin/users/${u.id}`} className="text-gray-400 hover:text-primary-600 text-xs">
                          View
                        </Link>
                        {!isSelf && (
                          <>
                            <button
                              onClick={() => void handleForceLogout(u.id)}
                              disabled={loggingOut === u.id}
                              className="text-amber-500 hover:text-amber-700 text-xs disabled:opacity-50"
                            >
                              {loggingOut === u.id ? '…' : 'Force Logout'}
                            </button>
                            <button
                              onClick={() => void handleDelete(u.id, u.email)}
                              disabled={deleting === u.id}
                              className="text-red-500 hover:text-red-700 text-xs disabled:opacity-50"
                            >
                              {deleting === u.id ? '…' : 'Delete'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
